<template>
  <div :class="$style.modal">
    <div :class="$style.modalContent">
      <ModalHeader
        :handleClose="handleClose"
        headerText="Change Password"
        :showClose="!userNeedsPWReset"
      />
      <div :class="$style.modalBody">
        <InputOptionalValidation
          v-model="currentPassword"
          v-validate="{
            required: true,
          }"
          autocomplete="off"
          :errorStyleExceptions="$style.error"
          label="Current Password"
          name="Current Password"
          type="password"
        />
        <InputOptionalValidation
          v-model="newPassword"
          v-validate="`${adminFieldValidations}|confirmed:Repeat New Password`"
          autocomplete="off"
          :errorStyleExceptions="$style.error"
          label="New Password"
          name="New Password"
          type="password"
          @input="runValidation"
        />
        <InputOptionalValidation
          v-model="confirmNewPassword"
          v-validate="`${adminFieldValidations}|confirmed:New Password`"
          autocomplete="off"
          :errorStyleExceptions="$style.error"
          label="Repeat New Password"
          name="Repeat New Password"
          type="password"
          @input="runValidation"
        />
      </div>
      <ModalFooter
        :cancelText="userNeedsPWReset ? '' : 'close'"
        :enableMainAction="enableMainAction"
        :handleClose="handleClose"
        :handleMainAction="handleMainAction"
        mainActionText="change"
      />
    </div>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { Validator } from 'vee-validate';
import { get, isEqual } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import InputOptionalValidation from 'components/FormValidation/Input.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ModalFooter from 'components/Modal/ModalComponents/ModalFooter.vue';
import ModalHeader from 'components/Modal/ModalComponents/ModalHeader.vue';
import UserApi from 'client/api/v2/UserApi';

const { error, success } = Constants.default;

export default {
  name: 'ChangePasswordModal',

  inject: ['$validator'],

  components: {
    InputOptionalValidation,
    ModalHeader,
    ModalFooter,
  },

  data: () => ({
    confirmNewPassword: '',
    currentPassword: '',
    newPassword: '',
  }),

  computed: {
    ...mapState('SettingsModule', [
      'userNeedsPWReset',
    ]),
    ...mapGetters('SettingsModule', {
      user: 'getUser',
    }),

    adminFieldValidations() {
      return 'required|min:12|max:64|noEmailPassword|noUserName|noWhitespace';
    },

    enableMainAction() {
      const invalidInputs = Object.keys(this.$validator.flags)
        .some(key => this.$validator.flags[key].invalid);

      return !invalidInputs;
    },
  },

  UserApi,

  created() {
    // Set so error messages display as 'The password confirmation does not match.'
    Validator.localize({
      en: {
        attributes: {
          'New Password': 'password',
          'Repeat New Password': 'password',
        },
      },
    });
  },

  beforeMount() {
    this.$validator.extend('noEmailPassword', {
      getMessage: 'Password may not contain your email address',
      validate: value => !value.toLowerCase().includes(this.user.email.toLowerCase()),
    });

    this.$validator.extend('noUserName', {
      getMessage: 'Password may not contain your username',
      validate: value => !value.toLowerCase().includes(this.user.email.split('@')[0].toLowerCase()),
    });

    this.$validator.extend('noWhitespace', {
      getMessage: 'Password cannot start or end with whitespace',
      validate: value => isEqual(value, value.trim()),
    });
  },

  methods: {
    handleClose() {
      ModalEventBus.close();
    },

    handleMainAction() {
      if (this.enableMainAction) {
        this.$options.UserApi.resetPassword({
          currentPassword: this.currentPassword,
          newPassword: this.newPassword,
          newPassword2: this.confirmNewPassword,
        })
          .then(() => {
            notify(success, 'Password changed');
          })
          .catch((err) => {
            notify(error, get(err, 'response.data.message', 'There was a problem changing your password.'));
          })
          .finally(() => {
            this.handleClose();
          });
      }
    },

    runValidation() {
      // If a user fixes a confirmation input, the error in the other input should disappear
      this.$validator.validateAll();
    },
  },
};
</script>

<style module>
@import "../Modal/css/ModalStyles.css";

.error {
  margin: 2px 0;
}
</style>
